import React, { useCallback, useMemo, useState } from 'react';
import { Workflow } from '../../store/project/projectSlice';
import { loadCanvas } from '../../services/repo';
import { be1Fetch } from '../../config/api';
import { showAlert, showConfirm } from '../common/AppDialog';
import {
  FirstFailure, RunLink, TaskInfo, WindowRun, dagIdOf, fetchRunDetail, fmtDuration, fmtMemory, fmtTaskRate, fmtTaskRows, fmtTime, isActiveState, memoryTitle, stateLabel, stateStyle, stopRun,
} from '../../services/executionApi';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../utils/formatters';
import { errorText } from '../../services/errorText';
import FirstFailureBanner, { NoNodeFailureNote } from './FirstFailureBanner';
import RunDetailModal, { RunRef } from './RunDetailModal';
import RunStateBadge from './RunStateBadge';
import ChildRunRows, { ChildToggle } from './ChildRunRows';

interface Props {
  /** 위 그래프가 읽은 그 구간의 실행 — 목록과 그래프가 어긋나지 않도록 데이터를 나눠 쓴다 */
  runs: WindowRun[];
  workflows: Workflow[];
  range: { start: Date; end: Date } | null;
  loading?: boolean;
  selectedWorkflowId?: string | null;
  onSelectWorkflow?: (w: Workflow) => void;
}

interface NodeInfo { label: string; type: string }

/**
 * 태스크 id → 노드 — 워크플로마다 한 번만 만든다.
 *
 * 태스크 id 는 라벨을 ASCII 로 줄인 것이라(한글은 지워진다) 그대로 두면 `transformload_ab12cd` 로 보인다.
 * 캔버스를 배포와 같은 방식으로 변환하면 노드→태스크 매핑이 나오므로, 그것을 뒤집어 노드 라벨을 찾는다.
 */
const nodeMapCache = new Map<string, Record<string, NodeInfo>>();

async function loadNodeMap(w: Workflow): Promise<Record<string, NodeInfo>> {
  const hit = nodeMapCache.get(w.id);
  if (hit) return hit;
  const doc = await loadCanvas(w.id, w.curVerNo ?? 0);
  const nodes = doc?.nodes || [];
  const res = await be1Fetch('/api/canvas/preview', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workflow_id: w.id, name: w.name, nodes, edges: doc?.edges || [], settings: doc?.settings }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errorText(body as any, `HTTP ${res.status}`));
  const out: Record<string, NodeInfo> = {};
  Object.entries((body.task_map || {}) as Record<string, string>).forEach(([nodeId, taskId]) => {
    const n = nodes.find((x: any) => x.id === nodeId);
    out[taskId] = { label: String(n?.data?.label || nodeId), type: String(n?.type || '') };
  });
  nodeMapCache.set(w.id, out);
  return out;
}

interface Expanded {
  tasks?: TaskInfo[];
  nodes?: Record<string, NodeInfo>;
  /** 실행 파라미터 — 구간 조회 응답에 없으면 실행 이력에서 보충한 값 */
  conf?: Record<string, unknown>;
  /** 처음 실패한 노드와 오류 문장 (실패가 없으면 null) */
  firstFailure?: FirstFailure | null;
  /** 이 실행을 부른 부모 실행 (실행 노드가 부른 경우) */
  parent?: RunLink | null;
  error?: string;
  loading: boolean;
}

/**
 * 위 그래프 구간 안의 실행 이력 — 워크플로별 실행 한 건이 한 줄이고, 펼치면 노드별로 나뉜다.
 *
 * 노드별 줄은 **태스크가 만들어지는 노드만** 나온다. SAM파일·DB테이블 같은 데이터 노드는 처리 노드가
 * 읽고 쓰는 대상이라 따로 실행되지 않는다(디자이너의 노드 뱃지도 같은 규칙이다).
 * 조회 전용이다 — 실행은 오른쪽 워크플로 목록에서 한다.
 */
const WindowRunHistory: React.FC<Props> = ({ runs, workflows, range, loading, selectedWorkflowId, onSelectWorkflow }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, Expanded>>({});
  /** 중지를 누른 실행 — 응답이 올 때까지 버튼을 잠근다 */
  const [stopping, setStopping] = useState<string | null>(null);
  /** 실행 상세 창 — 노드를 누르면 그 노드 로그, 자식·부모 실행으로 오간다 */
  const [viewing, setViewing] = useState<{ ref: RunRef; labels?: Record<string, string> } | null>(null);
  /** 펼친 실행 노드 — 그 아래로 자식 실행의 노드들을 보인다 (실행키/노드) */
  const [treeOpen, setTreeOpen] = useState<Record<string, boolean>>({});

  /** 실행 즉시 중지 — 도는 태스크를 끊는다. 목록은 위 그래프가 다음 주기에 다시 읽어 상태가 바뀐다 */
  const stopIt = async (w: Workflow, run: WindowRun, key: string) => {
    if (!(await showConfirm(t('execution:tree.stopConfirm', { name: w.name }), { title: t('execution:tree.stopTitle'), okText: t('execution:tree.stopOk') }))) return;
    setStopping(key);
    try {
      const r = await stopRun(w.id, run.dag_run_id);
      showAlert(`${t('execution:wh.stopRequested')}${r.stopped_tasks?.length ? t('execution:wh.stoppedTasks', { n: r.stopped_tasks.length }) : ''}`
        + (r.stopped_children?.length ? t('execution:wh.stoppedChildren', { names: r.stopped_children.map((c) => c.dag_id).join(', ') }) : ''), { title: w.name });
    } catch (e) {
      showAlert(t('execution:tree.stopFailed', { message: (e as Error).message }), { title: w.name, tone: 'danger' });
    } finally {
      setStopping(null);
    }
  };

  const byDag = useMemo(() => new Map(workflows.map((w) => [dagIdOf(w.id), w])), [workflows]);

  /** 그래프와 같은 기준: 시작 시각 역순 */
  const rows = useMemo(() => runs.slice().sort((a, b) => {
    const ts = (v?: string | null) => (v ? new Date(v).getTime() : 0);
    return ts(b.start_date) - ts(a.start_date);
  }), [runs]);

  const toggle = useCallback(async (run: WindowRun) => {
    const key = `${run.dag_id}/${run.dag_run_id}`;
    if (open === key) { setOpen(null); return; }
    setOpen(key);
    if (detail[key]) return;
    const w = byDag.get(run.dag_id);
    if (!w) { setDetail((p) => ({ ...p, [key]: { loading: false, error: t('execution:wh.notInProject') } })); return; }
    setDetail((p) => ({ ...p, [key]: { loading: true } }));
    try {
      // 노드 이름을 못 찾아도 태스크 목록은 보여 준다 — 캔버스를 못 읽는 경우가 더 드물다.
      // 파라미터(conf)는 구간 조회 응답에 있으면 그것을 쓰고, 없으면(옛 백엔드) 실행 이력에서 보충한다.
      const [d, nodes, conf] = await Promise.all([
        fetchRunDetail(w.id, run.dag_run_id),
        loadNodeMap(w).catch(() => ({} as Record<string, NodeInfo>)),
        run.conf ? Promise.resolve(run.conf) : (async () => {
          try {
            const r = await be1Fetch(`/api/canvas/${run.dag_id}/runs?limit=50`);
            const j = await r.json();
            return (j.runs || []).find((x: any) => x.dag_run_id === run.dag_run_id)?.conf as Record<string, unknown> | undefined;
          } catch { return undefined; }
        })(),
      ]);
      setDetail((p) => ({ ...p, [key]: { loading: false, tasks: d.tasks, nodes, conf, firstFailure: d.firstFailure, parent: d.parent } }));
    } catch (e) {
      setDetail((p) => ({ ...p, [key]: { loading: false, error: (e as Error).message } }));
    }
  }, [open, detail, byDag, t]);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
        <span className="font-medium">{t('execution:wh.title')} {rows.length > 0 ? t('execution:wh.count', { n: rows.length }) : ''}</span>
        <span className="text-xs text-gray-500">
          {t('execution:wh.sameRange')}{range ? ` (${fmtTime(range.start.toISOString()).slice(5, 16)} ~ ${fmtTime(range.end.toISOString()).slice(5, 16)})` : ''}{t('execution:wh.expandHint')}
        </span>
      </div>

      {/* 구간에 실행이 없어도 자리를 지킨다 — 구간을 바꿀 때마다 아래 내용이 튀지 않도록 */}
      <div className="max-h-[420px] overflow-auto" style={{ minHeight: '10rem' }}>
        {rows.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-500">
            {loading ? t('execution:wh.reading') : t('execution:wh.noRuns')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {rows.map((run) => {
              const key = `${run.dag_id}/${run.dag_run_id}`;
              const w = byDag.get(run.dag_id);
              const isOpen = open === key;
              const d = detail[key];
              return (
                <div key={key} className={selectedWorkflowId && w?.id === selectedWorkflowId ? 'bg-blue-50/40' : ''}>
                  <div className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 ${isOpen ? 'bg-gray-50' : ''}`}>
                    <button type="button" className="text-gray-400 hover:text-gray-700 w-4 shrink-0" onClick={() => toggle(run)}
                      title={isOpen ? t('execution:wh.collapse') : t('execution:wh.expand')}>
                      {isOpen ? '▾' : '▸'}
                    </button>
                    <button type="button" className="flex items-center gap-1.5 min-w-0 text-left" onClick={() => w && onSelectWorkflow?.(w)}
                      title={w ? t('execution:wh.selectWorkflow', { name: w.name }) : run.dag_id}>
                      <span className="shrink-0">{w?.icon || '📋'}</span>
                      <span className="text-sm text-gray-800 truncate max-w-[260px]">{w?.name || run.dag_id}</span>
                    </button>
                    <RunStateBadge state={run.state} handled={run.handled_failure} className="px-1.5 py-0.5 rounded-full text-xs font-semibold shrink-0" />
                    <span className="text-xs text-gray-600 shrink-0">{fmtTime(run.start_date)}</span>
                    <span className="ml-auto text-xs text-gray-400 shrink-0">
                      {isActiveState(run.state) ? t('execution:wh.inProgress') : fmtDuration(run.start_date, run.end_date)}
                    </span>
                    {/* 도는 실행만 중지할 수 있다 — 누르면 도는 태스크를 끊고 실행을 실패로 닫는다 */}
                    {isActiveState(run.state) && w && (
                      <button
                        type="button"
                        className="px-2 py-0.5 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 shrink-0"
                        disabled={stopping === key}
                        title={t('execution:wh.stopHint')}
                        onClick={(e) => { e.stopPropagation(); stopIt(w, run, key); }}
                      >
                        {stopping === key ? t('execution:wh.stopping') : t('execution:wh.stop')}
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <div className="px-8 pb-2 text-xs">
                      {/* 실행 파라미터 — 무슨 값으로 돌린 실행인지가 노드별 결과를 읽는 전제가 된다 */}
                      <div className="py-1">
                        <span className="text-gray-500 mr-1">{t('execution:wh.params')}</span>
                        {(() => {
                          const conf = (run.conf && Object.keys(run.conf).length ? run.conf : d?.conf) || null;
                          if (!conf || Object.keys(conf).length === 0) return <span className="text-gray-400">{t('execution:wh.none')}</span>;
                          return Object.entries(conf).map(([k, v]) => (
                            <span key={k} className="mr-2 font-mono text-gray-700">{k}={String(v)}</span>
                          ));
                        })()}
                      </div>
                      {!d || d.loading ? <div className="py-1 text-gray-400">{t('execution:wh.readingNodes')}</div>
                        : d.error ? <div className="py-1 text-red-600">{d.error}</div>
                          : !d.tasks || d.tasks.length === 0 ? <div className="py-1 text-gray-400">{t('execution:wh.noNodes')}</div>
                            : (
                              <>
                              {d.parent && (
                                <div className="mx-2 my-1.5 px-2.5 py-1.5 rounded border border-blue-200 bg-blue-50 text-gray-700 flex items-center gap-2">
                                  <span className="shrink-0 text-blue-700 font-semibold">{t('execution:detail.calledBy')}</span>
                                  <span className="truncate">{d.parent.name || d.parent.dag_id} › {d.parent.task_id}</span>
                                  <button type="button" className="ml-auto shrink-0 text-blue-700 hover:underline"
                                    onClick={() => d.parent && setViewing({ ref: { dagId: d.parent.dag_id, runId: d.parent.run_id, name: d.parent.name, taskId: d.parent.task_id } })}>
                                    {t('execution:detail.openParent')}
                                  </button>
                                </div>
                              )}
                              {!d.firstFailure && run.state === 'failed' && <NoNodeFailureNote />}
                              {d.firstFailure && (
                                <FirstFailureBanner failure={d.firstFailure} label={d.nodes?.[d.firstFailure.task_id]?.label || d.firstFailure.task_id}
                                  onOpenLog={() => setViewing({ ref: { dagId: run.dag_id, runId: run.dag_run_id, name: w?.name, taskId: d.firstFailure?.task_id }, labels: labelsOf(d) })} />
                              )}
                              <table className="w-full">
                                <tbody>
                                  {d.tasks.map((task) => {
                                    const n = d.nodes?.[task.task_id];
                                    const tkey = `${key}/${task.task_id}`;
                                    return (
                                      <React.Fragment key={task.task_id}>
                                      <tr className="border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50"
                                        title={t('execution:detail.clickForLog')}
                                        onClick={() => setViewing({ ref: { dagId: run.dag_id, runId: run.dag_run_id, name: w?.name, taskId: task.task_id }, labels: labelsOf(d) })}>
                                        <td className="py-0.5 pr-2 text-gray-700 truncate max-w-[280px]" title={task.task_id}>
                                          {task.child && <ChildToggle open={!!treeOpen[tkey]} onToggle={() => setTreeOpen((p) => ({ ...p, [tkey]: !p[tkey] }))} />}
                                          {n ? n.label : task.task_id}
                                          {n?.type && <span className="ml-1 text-gray-400">{n.type}</span>}
                                          {task.child && (
                                            <button type="button" className="ml-2 text-blue-700 hover:underline"
                                              onClick={(e) => { e.stopPropagation(); task.child && setViewing({ ref: { dagId: task.child.dag_id, runId: task.child.run_id, name: task.child.name } }); }}>
                                              {t('execution:detail.openChild', { name: task.child.name || task.child.dag_id })}
                                            </button>
                                          )}
                                        </td>
                                        <td className="py-0.5 pr-2 w-20">
                                          <span className={`px-1.5 rounded ${stateStyle(task.state)}`}>{stateLabel(task.state)}</span>
                                        </td>
                                        <td className="py-0.5 text-right text-gray-400 w-16">
                                          {task.duration != null ? formatDuration(Number(task.duration)) : '-'}
                                        </td>
                                        <td className="py-0.5 pl-2 text-right text-gray-400 w-20 tabular-nums" title={t('execution:rows.colTitle')}>
                                          {fmtTaskRows(task)}
                                        </td>
                                        <td className="py-0.5 pl-2 text-right text-gray-400 w-20 tabular-nums" title={t('execution:rows.rateTitle')}>
                                          {fmtTaskRate(task)}
                                        </td>
                                        <td className="py-0.5 pl-2 text-right text-gray-400 w-16 tabular-nums" title={memoryTitle(task.memory)}>
                                          {fmtMemory(task.memory)}
                                        </td>
                                      </tr>
                                      {task.child && treeOpen[tkey] && (
                                        <ChildRunRows link={task.child} depth={1} onOpen={(ref) => setViewing({ ref })} />
                                      )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                              </>
                            )}
                      <div className="mt-1 text-[0.6875rem] text-gray-400">
                        {t('execution:wh.dataNodesNote')}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {viewing && <RunDetailModal start={viewing.ref} labels={viewing.labels} onClose={() => setViewing(null)} />}
    </div>
  );
};

/** 노드 task_id → 화면 이름 (실행 상세 창에 넘긴다) */
const labelsOf = (d: Expanded): Record<string, string> =>
  Object.fromEntries(Object.entries(d.nodes || {}).map(([k, v]) => [k, v.label]));

export default WindowRunHistory;
